import { ICommand } from '../../../core/commands/ICommand';
import { CreateShapeCommand } from '../../../core/commands/CreateShapeCommand';
import type { GatewayType } from '../shapes/flowchart/flowchartShapes.manifest';
import { createNodeId, type NodeAddition } from '../../../core/data-profiles/scenarios/ScenarioDelta';
import type { CommandDeltaContribution, IScenarioRecordable } from '../../../core/data-profiles/scenarios/ScenarioRecordable';
import { getTranslatedFlowchartShapeLabel } from '../shapes/flowchart/flowchartShapeI18n';

export interface CreateDecisionParams {
  initialStyle?: Readonly<Record<string, unknown>>;
  x: number;
  y: number;
  width?: number;
  height?: number;
  label?: string;
  description?: string;
  gatewayType?: GatewayType;
}

export class CreateDecisionCommand implements ICommand, IScenarioRecordable {
  id: string;
  name = 'Create Decision';
  description?: string;
  timestamp: number;
  readonly isScenarioRecordable = true as const;

  private params: CreateDecisionParams;
  private inner?: CreateShapeCommand;
  private scenarioNodeId: ReturnType<typeof createNodeId>;

  constructor(params: CreateDecisionParams) {
    this.id = `create-decision-${Date.now()}`;
    this.timestamp = Date.now();
    this.params = params;
    this.description = 'Create a decision gateway';
    this.scenarioNodeId = createNodeId(`scenario-${this.id}`);
  }

  execute(): void {
    const attrs: Record<string, unknown> = {
      label: this.params.label ?? getTranslatedFlowchartShapeLabel('decision'),
      description: this.params.description ?? '',
      gatewayType: this.params.gatewayType ?? 'exclusive',
    };

    this.inner = new CreateShapeCommand({
      initialStyle: this.params.initialStyle,
      type: 'decision',
      category: 'flowchart',
      x: this.params.x,
      y: this.params.y,
      width: this.params.width,
      height: this.params.height,
      attrs,
    } as any);

    this.inner.execute();
  }

  undo(): void {
    this.inner?.undo();
  }

  redo(): void {
    this.inner?.redo();
  }

  getCreatedId(): string | undefined {
    return this.inner?.getCreatedId?.();
  }

  toScenarioDelta(): CommandDeltaContribution {
    const label = this.params.label ?? getTranslatedFlowchartShapeLabel('decision');
    const props: Record<string, unknown> = {
      description: this.params.description ?? '',
      gatewayType: this.params.gatewayType ?? 'exclusive',
    };

    const addition: NodeAddition = {
      id: this.scenarioNodeId,
      type: 'decision',
      name: label,
      position: { x: this.params.x, y: this.params.y },
      props,
    };

    return { additions: [addition] };
  }

  getUndoDelta(): CommandDeltaContribution {
    return {
      removals: [
        {
          nodeId: this.scenarioNodeId,
          originalData: {
            name: this.params.label ?? getTranslatedFlowchartShapeLabel('decision'),
            type: 'decision',
          },
        },
      ],
    };
  }
}
